import { Spin } from 'antd';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';

if (pdfjs.version) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

const PRINT_IFRAME_CLEANUP_DELAY_MS = 60_000;

export interface PdfViewerProps {
  url: string;
  scale?: number;
  fitToContainer?: boolean;
  showPagination?: boolean;
  renderAllPages?: boolean;
  loadingComponent?: React.ReactNode;
  containerStyle?: React.CSSProperties;
  canvasStyle?: React.CSSProperties;
  paginationStyle?: React.CSSProperties;
  previousLabel?: string;
  nextLabel?: string;
  onLoadSuccess?: (numPages: number) => void;
  onLoadError?: (error: Error) => void;
  onPageChange?: (currentPage: number, totalPages: number) => void;
}

export interface PdfViewerHandle {
  print: () => Promise<void>;
}

export const PdfViewer = forwardRef<PdfViewerHandle, PdfViewerProps>(
  (
    {
      url,
      scale = 1.5,
      fitToContainer = false,
      showPagination = true,
      renderAllPages = false,
      loadingComponent,
      containerStyle,
      canvasStyle,
      paginationStyle,
      previousLabel = 'Previous',
      nextLabel = 'Next',
      onLoadSuccess,
      onLoadError,
      onPageChange
    },
    ref
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const pageCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const pdfDocRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
    const printIframeRef = useRef<HTMLIFrameElement | null>(null);
    const printBlobUrlRef = useRef<string | null>(null);

    const renderPageToCanvas = useCallback(
      async (pdf: pdfjs.PDFDocumentProxy, pageNum: number, canvas: HTMLCanvasElement | null) => {
        const page = await pdf.getPage(pageNum);
        if (!canvas) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        let effectiveScale = scale;
        if (fitToContainer) {
          const container = containerRef.current;
          if (container && container.clientWidth > 0 && container.clientHeight > 0) {
            const baseViewport = page.getViewport({ scale: 1 });
            if (baseViewport?.width && baseViewport?.height) {
              const widthScale = container.clientWidth / baseViewport.width;
              const heightScale = container.clientHeight / baseViewport.height;
              effectiveScale = Math.min(widthScale, heightScale);
            }
          }
        }

        const viewport = page.getViewport({ scale: effectiveScale });
        if (!viewport?.height || !viewport?.width) return;

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: context,
          viewport
        }).promise;
      },
      [scale, fitToContainer]
    );

    const renderPage = useCallback(
      (pdf: pdfjs.PDFDocumentProxy, pageNum: number) => renderPageToCanvas(pdf, pageNum, canvasRef.current),
      [renderPageToCanvas]
    );

    useImperativeHandle(
      ref,
      () => ({
        print: async () => {
          const pdf = pdfDocRef.current;
          if (!pdf) return;

          const data = await pdf.getData();
          const blob = new Blob([data], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);

          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed';
          iframe.style.right = '0';
          iframe.style.bottom = '0';
          iframe.style.width = '0';
          iframe.style.height = '0';
          iframe.style.border = '0';

          printIframeRef.current = iframe;
          printBlobUrlRef.current = blobUrl;

          let cleanedUp = false;
          const cleanup = () => {
            if (cleanedUp) return;
            cleanedUp = true;
            URL.revokeObjectURL(blobUrl);
            iframe.remove();
            globalThis.removeEventListener('afterprint', cleanup);
            if (printIframeRef.current === iframe) printIframeRef.current = null;
            if (printBlobUrlRef.current === blobUrl) printBlobUrlRef.current = null;
          };

          iframe.onload = () => {
            try {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
            } catch {
              cleanup();
            }
          };

          globalThis.addEventListener('afterprint', cleanup);
          setTimeout(cleanup, PRINT_IFRAME_CLEANUP_DELAY_MS);

          iframe.src = blobUrl;
          document.body.appendChild(iframe);
        }
      }),
      []
    );

    useEffect(
      () => () => {
        if (printBlobUrlRef.current) {
          URL.revokeObjectURL(printBlobUrlRef.current);
          printBlobUrlRef.current = null;
        }
        if (printIframeRef.current) {
          printIframeRef.current.remove();
          printIframeRef.current = null;
        }
      },
      []
    );

    useEffect(() => {
      if (!url) return;

      const loadPdf = async () => {
        try {
          setLoading(true);
          const pdf = await pdfjs.getDocument(url).promise;
          if (!pdf?.numPages) {
            throw new Error('Invalid PDF: unable to determine number of pages');
          }
          pdfDocRef.current = pdf;
          setTotalPages(pdf.numPages);
          setCurrentPage(1);
          if (!renderAllPages) {
            await renderPage(pdf, 1);
          }
          setLoading(false);
          onLoadSuccess?.(pdf.numPages);
        } catch (error) {
          setLoading(false);
          onLoadError?.(error as Error);
        }
      };

      loadPdf();

      return () => {
        pdfDocRef.current?.destroy();
        pdfDocRef.current = null;
      };
    }, [onLoadError, onLoadSuccess, renderAllPages, renderPage, url]);

    useEffect(() => {
      const pdf = pdfDocRef.current;
      if (!renderAllPages || loading || !pdf) return;

      pageCanvasRefs.current.length = totalPages;
      let cancelled = false;

      const renderEveryPage = async () => {
        for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
          if (cancelled) return;
          await renderPageToCanvas(pdf, pageNumber, pageCanvasRefs.current[pageNumber - 1]);
        }
      };

      renderEveryPage();

      return () => {
        cancelled = true;
      };
    }, [renderAllPages, loading, totalPages, renderPageToCanvas]);

    const handlePageChange = async (newPage: number) => {
      if (!pdfDocRef.current || newPage < 1 || newPage > totalPages) return;
      setCurrentPage(newPage);
      await renderPage(pdfDocRef.current, newPage);
      onPageChange?.(newPage, totalPages);
    };

    const defaultContainerStyle: React.CSSProperties = {
      backgroundColor: '#f5f5f5',
      flexDirection: 'column',
      alignItems: 'center',
      overflow: 'auto',
      display: 'flex',
      height: '100%',
      width: '100%',
      ...(renderAllPages && { gap: '16px', padding: '16px 0' }),
      ...containerStyle
    };

    const defaultCanvasStyle: React.CSSProperties = {
      display: loading ? 'none' : 'block',
      maxWidth: '100%',
      ...canvasStyle
    };

    const defaultPaginationStyle: React.CSSProperties = {
      padding: '10px',
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
      ...paginationStyle
    };

    return (
      <div ref={containerRef} style={defaultContainerStyle}>
        {loading && (loadingComponent || <Spin size='large' style={{ margin: '20px auto' }} />)}
        {renderAllPages ? (
          !loading &&
          Array.from({ length: totalPages }, (_, pageIndex) => (
            <canvas
              key={pageIndex}
              ref={canvasElement => {
                pageCanvasRefs.current[pageIndex] = canvasElement;
              }}
              style={defaultCanvasStyle}
            />
          ))
        ) : (
          <canvas ref={canvasRef} style={defaultCanvasStyle} />
        )}
        {!loading && !renderAllPages && showPagination && totalPages > 1 && (
          <div style={defaultPaginationStyle}>
            <button type='button' onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}>
              {previousLabel ?? 'Previous'}
            </button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button type='button' onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages}>
              {nextLabel ?? 'Next'}
            </button>
          </div>
        )}
      </div>
    );
  }
);

PdfViewer.displayName = 'PdfViewer';
